import {Container, Sprite} from '@pixi/react';
import {Assets, DisplayObject, FederatedPointerEvent, Rectangle} from 'pixi.js';
import {ReactNode, useMemo} from 'react';
import {Scene} from './Scene';
import {GAME_BACKGROUND, OVERLAY} from '../assetManifest';
import {DesignLink} from '../pixijs/DesignLink';
import {DesignPlayer} from '../pixijs/DesignPlayer';
import {ObjectivePanel} from '../pixijs/ObjectivePanel';
import {useActiveEditMode, useClearActiveEdit} from '../state/networkEditStore';
import {useAddDesignNode, useNetworkLinks, useNetworkNodes, useNetworkOwnership} from '../state/networkHooks';
import {NodeId, PositionNode} from '../state/networkStore';
import {useInitialNetworkMade} from '../state/uiStore';


interface UnsortedRows {
    [y: number]: NodeId[];
}

export function Design() {
    const nodes = useNetworkNodes();
    const links = useNetworkLinks();
    const ownership = useNetworkOwnership();
    const addNode = useAddDesignNode();
    const activeEditMode = useActiveEditMode();
    const clearActiveEdit = useClearActiveEdit();
    const initialNetworkSelectionMade = useInitialNetworkMade();

    const linksByY = useMemo(()=> {
        return links.map(link => {
            if (!(link.nodeIdA in nodes) || !(link.nodeIdB in nodes))
                throw new Error(`Design Scene - Illegal state. Link references a node that isn't in the network. ${link.nodeIdA} to ${link.nodeIdB}`);
            return {
                // ordered by minimum y
                rows: [nodes[link.nodeIdA].y, nodes[link.nodeIdB].y].sort(),
                link,
            };
        });
    }, [links, nodes]);

    const unsortedRows: UnsortedRows = useMemo(() => {
        const grouped: UnsortedRows = {};
        // Make into more usable shape
        Object.entries(nodes).forEach(([idStr, node]: [string, PositionNode]) => {
            const key = node.y; // grouping by y value
            const id = parseInt(idStr);
            if (!grouped[key])
                grouped[key] = []; // new row/y
            grouped[key].push(id);
        });
        return Object.keys(grouped)
            .reduce((acc, y) => {
                acc[Number(y)] = grouped[Number(y)];
                return acc;
            }, {} as UnsortedRows);
    }, [nodes]);

    const playersAndLinks: ReactNode[] = useMemo(() => {
        const elements: ReactNode[] = [];

        const rows: [keyof UnsortedRows] = Object.keys(unsortedRows) as unknown as [keyof UnsortedRows];
        rows.sort().forEach(y => {
            const nodeIds: NodeId[] = unsortedRows[y];
            const sameRowLinks: ReactNode[] = linksByY
                .filter((link) => link.rows[0] === Number(y) && link.rows[1] === Number(y))
                .map((link, idx) => (
                    <DesignLink
                        key={`link_${link.link.nodeIdA}_${link.link.nodeIdB}_${idx}`}
                        nodeAId={link.link.nodeIdA}
                        nodeBId={link.link.nodeIdB}
                        relationship={link.link.relationship}
                    />
                ));

            const playerSprites: ReactNode[] = nodeIds.map(nodeId => {
                const node = nodes[nodeId];
                const playerId = ownership[nodeId] ?? undefined;
                return (
                    <DesignPlayer
                        key={`player_${nodeId}`}
                        nodeId={nodeId}
                        playerId={playerId}
                        position={{x: node.x, y: node.y}}
                    />
                );
            });

            const nextRowLinks: ReactNode[] = linksByY
                .filter((link) => link.rows[0] === Number(y) && link.rows[1] !== Number(y))
                .map((link, idx) => (
                    <DesignLink
                        key={`link_${link.link.nodeIdA}_${link.link.nodeIdB}_${idx}`}
                        nodeAId={link.link.nodeIdA}
                        nodeBId={link.link.nodeIdB}
                        relationship={link.link.relationship}
                    />
                ));

            elements.push(...sameRowLinks, ...playerSprites, ...nextRowLinks);
        });

        return elements;
    }, [unsortedRows, linksByY, nodes, ownership]);

    return (
        <Scene>
            <Sprite texture={Assets.get(GAME_BACKGROUND)} />
            {initialNetworkSelectionMade && <ObjectivePanel/>}
            <Container
                interactive
                hitArea={new Rectangle(0, 0, 1600, 1080)}
                onpointerdown={(event: FederatedPointerEvent) => {
                    if (activeEditMode === 'addPlayer') {   
                        const localPos = event.getLocalPosition(event.currentTarget as DisplayObject);
                        addNode(localPos);
                        clearActiveEdit();
                    }
                }}
            >
                {playersAndLinks}
            </Container>
            <Sprite texture={Assets.get(OVERLAY)} eventMode={'none'}/>
        </Scene>
    );
}
